import { type Database } from "@fitness-app/supabase";

import { type AddProgramAutomationToClient, type AppAccessType } from "./Automation";
import { type DietaryPreference } from "./MealsPlan";
import { type ProductClient, type SubscriptionProductClient } from "./ProductClient";
import { type Gender } from "./shared";
import { type TeamMemberChatAgentConfig } from "./TeamMember";
import { type TraineeSettings } from "./TraineeSettings";
import { type TraineeFeaturesFlags } from "./TrainerConfig";

export type TraineeEntity = Database["public"]["Tables"]["trainee"]["Row"];

export enum TraineeStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  TERMINATED = "TERMINATED",
  ARCHIVED = "ARCHIVED",
}

// tenantId is trigger-maintained plumbing for RLS/authz fences — kept out of the
// domain entity so trainee construction sites are not forced to supply it.
export interface BaseTrainee extends Omit<TraineeEntity, "gender" | "status" | "metadata" | "type" | "tags" | "tenantId"> {
  gender: Gender.MALE | Gender.FEMALE | null;
  status: TraineeStatus;
  metadata?: Record<string, null | string | number | object[] | object> & {
    automationToApply?: AddProgramAutomationToClient["programAutomation"][] | null;
    phoneNumber?: string | number | null;
    flags?: TraineeFeaturesFlags;
  };
  type: AppAccessType;
  dietaryPreference: DietaryPreference | null;
  tags: string[];
}

export interface TraineeWithFullAccess extends BaseTrainee {
  type: AppAccessType;
}

export interface TraineeWithLimitedAccess extends BaseTrainee {
  type: AppAccessType;
}

export interface AssignedTrainer {
  userId: string | null;
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  slackId: string | null;
  chatAgentConfig?: TeamMemberChatAgentConfig | null;
}

export type Trainee = TraineeWithFullAccess | TraineeWithLimitedAccess;

export type TraineeWithAssignedTrainer = Trainee & {
  assignedTrainer: (AssignedTrainer & { profile?: { avatarUrl?: string | null } }) | null;
};

export type TraineeWithAssignedTrainerAndProduct = TraineeWithAssignedTrainer & {
  productClient: {
    // Nullable for the same reason as in TraineeProfile below: the embedded
    // product comes from the catalog table, which RLS closes for anyone without
    // products.read ALL (trainers, sales reps, or a stale JWT after a grant).
    product: { name: string } | null;
    productId: string;
  } | null;
};

export type TraineeProfile = TraineeWithAssignedTrainer & {
  user: { avatarUrl: string; firstName: string; lastName: string } | null;
} & {
  productClient: {
    // Nullable: sales reps have no read on the product catalog table (TRE-1636),
    // so the embedded product resolves to null under RLS.
    product: { name: string } | null;
    productId: string;
    status: ProductClient["status"];
    accessPeriod: ProductClient["accessPeriod"];
    productDuration: ProductClient["productDuration"];
    scheduledAccessPeriod: SubscriptionProductClient["scheduledAccessPeriod"];
    phoneNumber: string | null;
  } | null;
  settings: null | { onboarding: TraineeSettings["onboarding"] };
  nutrition: { id: string; name: string; startAt: string | null } | null;
};
